import { useMemo, useRef } from 'react';

/**
 * Keeps rows in the order they were first shown while the caller stays mounted. Quick picks
 * such as "Recent" are sorted by last use, so a one-tap add would otherwise move the row the
 * user just tapped (and shift every row above it). Rows seen for the first time go last.
 */
export function useStableOrder<T>(rows: readonly T[] | undefined, key: (row: T) => string): T[] {
  const order = useRef(new Map<string, number>());
  return useMemo(() => {
    const seen = order.current;
    for (const row of rows ?? []) {
      const k = key(row);
      if (!seen.has(k)) seen.set(k, seen.size);
    }
    return [...(rows ?? [])].sort((a, b) => seen.get(key(a))! - seen.get(key(b))!);
    // `key` is a projection, not data: only new rows change the result.
  }, [rows]);
}
